import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartXAxis,
  ChartXAxisItem,
} from "@progress/kendo-react-charts";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { Error } from "@progress/kendo-react-labels";
import { Fragment, useEffect, useState } from "react";
import quotesApi from "../../api/quotes";
import { useApi } from "../../hooks/useApi";
import { LoadingIndicator } from "../LoadingIndicator";
import { HeaderTextSelector } from "../utilities";

interface IQuotesStatusHeatChartProps {
  dates: SelectionRange;
}

export const QuotesStatusHeatMapChart = (props: IQuotesStatusHeatChartProps) => {
  const { dates } = props;

  const getQuotesApi = useApi(quotesApi.getStatusDetails);
  const [data, setData] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [commodities, setCommodities] = useState<any>({});
  const [products, setProducts] = useState<any>({});
  const [chartData, setChartData] = useState<any[]>([]);
  const [selected, setSelected] = useState("tickerCode");

  const yOptions: any = {
    Commodity: "tickerCode",
    Product: "instrumentCode",
    Company: "companyName",
    User: "user",
  };

  useEffect(() => {
    getQuotesApi.request(dates);
  }, [dates]);

  useEffect(() => {
    const data = getQuotesApi.data;
    if (!data) return;

    setData(data.data ?? []);
    setStatuses(Object.values(data.statuses ?? {}));
    setCommodities(data.commodities ?? []);
    setProducts(data.products ?? []);
  }, [getQuotesApi.data]);

  useEffect(() => {
    if (data.length && statuses.length) {
      const totals = createChartData(selected);
      const keys = Object.keys(totals).sort();
      setChartData(keys.map((k: string) => totals[k]));
    }
  }, [data, statuses, selected]);

  const createChartData = (field: string) => {
    return data.reduce((acc: any, cur: any) => {
      const key = `${cur.status}_${cur[field]}`;
      if (acc[key]) acc[key].value++;
      else acc[key] = { x: cur[field], y: cur.status, value: 1 };
      return acc;
    }, {});
  };

  const renderTooltip = ({ point }: any) => (
    <Fragment>
      <span>
        {selected === yOptions.Commodity
          ? commodities[point.value.x]
          : selected === yOptions.Product
            ? products[point.value.x]
            : point.value.x}
        , {point.value.y}: {point.value.value}
      </span>
    </Fragment>
  );

  const formatLabel = (e: any) => breakUpString(e.value, 10);

  const breakUpString = (input: string, maxCharsPerLine: number): string => {
    if (maxCharsPerLine <= 0) return input;

    const words = input.split(" ");
    let currentLine = "";
    let result = "";

    for (const word of words) {
      if (currentLine.length + word.length + (currentLine ? 1 : 0) <= maxCharsPerLine) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        result += currentLine + "\n";
        currentLine = word;
      }
    }

    if (currentLine) result += currentLine;

    return result;
  };

  return (
    <>
      <LoadingIndicator loading={getQuotesApi.loading} />
      {getQuotesApi.error && <Error>{getQuotesApi.error}</Error>}

      {data && data.length > 0 && (
        <HeaderTextSelector
          values={Object.keys(yOptions)}
          selected={Object.keys(yOptions).find((key) => yOptions[key] === selected)}
          onChange={(e) => setSelected(yOptions[e])}
        />
      )}

      {!getQuotesApi.loading && data && (
        <>
          {data.length === 0 && <div>No data to display</div>}
          {data.length > 0 && (
            <Chart
              pannable={{ lock: "y" }}
              zoomable={{ mousewheel: { lock: "y" }, selection: { lock: "y" } }}
              style={{ height: "500px", width: "100%" }}
            >
              <ChartSeries>
                <ChartSeriesItem
                  type="heatmap"
                  data={chartData}
                  xField="x"
                  yField="y"
                  field="value"
                  labels={{
                    visible: true,
                  }}
                  markers={{
                    type: "roundedRect",
                    border: {
                      width: 2,
                    },
                  }}
                />
              </ChartSeries>
              <ChartXAxis>
                <ChartXAxisItem labels={{ rotation: "auto", content: formatLabel }} />
              </ChartXAxis>
              <ChartTooltip render={renderTooltip} />
            </Chart>
          )}
        </>
      )}
    </>
  );
};
